import { createContext, useMemo, useState } from "react";
import { trpc } from "../trpc";
import posthog from "../lib/posthog";

type UserContextType = {
  roles: string[];
  email?: string;
  memberId?: string;
  userId?: string;
  employeeId?: string;
  employees: Array<{ id: string; name: string }>;
  employeeIds: string[];
  pipelinePermissions: Array<{ id: string }>;
  // Decided by the backend (auth.getUserInfo): the external allowlist is a secret the
  // portal cannot read. Gates the Palomma-only configuration screens.
  hasPalommaOnlyAccess: boolean;
  isLoading: boolean;
};

const UserContext = createContext<UserContextType>({
  roles: [],
  email: undefined,
  memberId: undefined,
  userId: undefined,
  employeeId: undefined,
  employees: [],
  employeeIds: [],
  pipelinePermissions: [],
  hasPalommaOnlyAccess: false,
  isLoading: true,
});

const UserContextProvider = ({ children }: React.PropsWithChildren): JSX.Element => {
  const [roles, setRoles] = useState<string[]>([]);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [memberId, setMemberId] = useState<string | undefined>(undefined);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [employeeId, setEmployeeId] = useState<string | undefined>(undefined);
  const [employees, setEmployees] = useState<Array<{ id: string; name: string }>>([]);
  const [pipelinePermissions, setPipelinePermissions] = useState<
    Array<{ id: string }>
  >([]);
  const [hasPalommaOnlyAccess, setHasPalommaOnlyAccess] = useState(false);

  // Fetch user info including roles and pipeline permissions
  const { isLoading: isLoadingUserInfo } = trpc.auth.getUserInfo.useQuery(undefined, {
    onSuccess: (data) => {
      setRoles(data.roles || []);
      setEmail(data.email);
      setMemberId(data.memberId);
      setUserId(data.userId);
      setEmployeeId(data.employeeId);
      setEmployees(data.employees || []);
      setPipelinePermissions(data.pipelinePermissions || []);
      setHasPalommaOnlyAccess(data.hasPalommaOnlyAccess);

      if (data.memberId) {
        posthog.identify(data.memberId, {
          email: data.email,
          merchant_id: data.organization.slug,
          roles: data.roles,
        });
      }
    },
  });

  const employeeIds = useMemo(() => employees.map((employee) => employee.id), [employees]);

  return (
    <UserContext.Provider
      value={{
        roles,
        email,
        memberId,
        userId,
        employeeId,
        employees,
        employeeIds,
        pipelinePermissions,
        hasPalommaOnlyAccess,
        isLoading: isLoadingUserInfo,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
export { UserContextProvider };
